import type { Session } from '@supabase/supabase-js'
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { type SessionSource, clientFor, pickSession } from './active-session'
import { type OrgPlan, loadOrgPlan } from './plan'
import { authClient, sharedAuthClient } from './supabase'
import { authCaptchaOptions, captchaErrorMessage } from './turnstile'

export interface Profile {
  user_id: string
  email: string | null
  plan: 'free' | 'pro'
  stripe_customer_id: string | null
  current_period_end: string | null
  api_key: string | null
}

interface AuthState {
  session: Session | null
  /** which project the active session belongs to; null when signed out */
  source: SessionSource | null
  profile: Profile | null
  loading: boolean
  isPro: boolean
  /** the plan the organisation holds (sales grant or workspace purchase); null until known */
  orgPlan: OrgPlan | null
  /** magic-link sign in — passwordless, so no credential ever touches the page */
  signIn: (email: string) => Promise<{ error: string | null }>
  /** OAuth sign in via the shared navo24 identity (one login across all four pillars) */
  signInWithProvider: (provider: OAuthProvider) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

export type OAuthProvider = 'google' | 'github' | 'apple'

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [source, setSource] = useState<SessionSource | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [orgPlan, setOrgPlan] = useState<OrgPlan | null>(null)
  const [loading, setLoading] = useState(true)

  async function loadProfile(src: SessionSource | null = source) {
    // The profile lives in the project that owns the session. With the shared-sign-in flag off this is
    // always authClient (src is 'identity'), exactly as before.
    const { data, error } = await clientFor(src ?? 'identity').rpc('frm_ensure_profile')
    if (!error && data) setProfile(data as Profile)
    // The organisation's plan is a second source of truth; a failure here must never hide the profile.
    setOrgPlan(await loadOrgPlan())
  }

  useEffect(() => {
    // Two sessions can coexist while sign-in moves to the shared project: the shared one wins, the old
    // identity one is still read (never cleared here), so the release signs nobody out.
    let shared: Session | null = null
    let identity: Session | null = null
    const apply = () => {
      const a = pickSession(shared, identity)
      setSession(a?.session ?? null)
      setSource(a?.source ?? null)
      return a
    }
    const sameClient = sharedAuthClient === authClient
    Promise.all([
      sharedAuthClient ? sharedAuthClient.auth.getSession().catch(() => null) : null,
      sameClient ? null : authClient.auth.getSession().catch(() => null),
    ]).then(([sh, id]) => {
      shared = sh?.data.session ?? null
      identity = id?.data.session ?? null
      const a = apply()
      if (a) loadProfile(a.source).finally(() => setLoading(false))
      else setLoading(false)
    })
    const onChange = (kind: SessionSource) => (_e: unknown, s: Session | null) => {
      if (kind === 'shared') shared = s
      else identity = s
      const a = apply()
      if (a) loadProfile(a.source)
      else {
        setProfile(null)
        setOrgPlan(null)
      }
    }
    const subs = [
      sharedAuthClient
        ? sharedAuthClient.auth.onAuthStateChange(onChange('shared')).data.subscription
        : null,
      sameClient ? null : authClient.auth.onAuthStateChange(onChange('identity')).data.subscription,
    ]
    return () => {
      for (const sub of subs) sub?.unsubscribe()
    }
  }, [])

  async function signIn(email: string) {
    // The captcha token rides along with the magic-link request. Supabase's "Enable CAPTCHA protection"
    // is a project-wide switch and this app shares TrackingMCP's project, so the day it is turned on a
    // tokenless request here is refused outright, not degraded. With no site key configured
    // authCaptchaOptions() returns {} and this behaves exactly as it did before.
    // New sign-ins go to the shared project when the flag is on (a shared account is created by the
    // project's own sign-up trigger), otherwise to the identity project as before.
    const { error } = await (sharedAuthClient ?? authClient).auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/account`,
        ...(await authCaptchaOptions()),
      },
    })
    return { error: error ? (captchaErrorMessage(error) ?? error.message) : null }
  }

  async function signInWithProvider(provider: OAuthProvider) {
    const { error } = await (sharedAuthClient ?? authClient).auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/account` },
    })
    return { error: error?.message ?? null }
  }

  async function signOut() {
    // An explicit sign-out ends every session this tab can read, or the old identity session would
    // resurface as "signed in" the moment the shared one is gone. 'local' on the identity project: it
    // only clears this browser and revokes nothing server side.
    if (sharedAuthClient && sharedAuthClient !== authClient) {
      await sharedAuthClient.auth.signOut().catch(() => {})
      await authClient.auth.signOut({ scope: 'local' }).catch(() => {})
    } else {
      await authClient.auth.signOut()
    }
    setProfile(null)
    setOrgPlan(null)
  }

  return (
    <Ctx.Provider
      value={{
        session,
        source,
        profile,
        loading,
        isPro: profile?.plan === 'pro' || orgPlan?.active === true,
        orgPlan,
        signIn,
        signInWithProvider,
        signOut,
        refresh: () => loadProfile(),
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useAuth(): AuthState {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAuth must be used within AuthProvider')
  return c
}
